import { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Phone } from 'lucide-react';

import { Breadcrumb } from '@/components/common/Breadcrumb';
import { PageHeader } from '@/components/common/PageHeader';
import { PolicyDeniedView } from '@/components/common/PolicyDeniedView';
import { QueryErrorView } from '@/components/common/QueryErrorView';
import { Can } from '@/components/permission/Can';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingRows } from '@/components/shared/LoadingRows';
import { useToast } from '@/components/shared/Toast';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { docId } from '@/features/agents/utils';
import { useGetFactoriesQuery } from '@/features/agents/api/agentsApi';
import { useGetOrgProjectsQuery } from '@/features/orgs/api/orgApi';
import { orgResource } from '@/features/permissions/model';
import { useCan } from '@/features/permissions/useCan';
import { BindAgentDialog } from '../components/BindAgentDialog';
import { EditNumberDialog } from '../components/EditNumberDialog';
import { NumbersTable } from '../components/NumbersTable';
import { RegisterNumberDialog } from '../components/RegisterNumberDialog';
import { useDeleteNumberMutation, useListNumbersQuery } from '../api/numbersApi';
import { NUMBERS_ACTIONS, type PhoneNumberView } from '../types';
import { numbersErrorMessage } from '../utils';

const ALL = 'all';
const PAGE_SIZE = 10;

/**
 * Phone numbers page (design §5/§6): `/organizations/:orgId/numbers`.
 *
 * Server-side page/limit pagination (`limit=10`); NO sort/search chrome — the
 * list API serves neither (audit #9, OQ-6). Filters are deep-linkable (§5/G4):
 * `?projectId=` and `?isActive=true|false` preselect the selects and filter
 * changes replace the URL (`replaceState` semantics). Without
 * `voice:numbers:read` the whole view is `PolicyDeniedView`; the header entry
 * point is hidden without `voice:numbers:create`, row actions are disabled +
 * Tooltip without their policies (§6.3).
 */
export function NumbersPage() {
  const { orgId = '' } = useParams<{ orgId: string }>();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const resource = orgResource(orgId);

  const canRead = useCan(NUMBERS_ACTIONS.read, resource);
  const canCreate = useCan(NUMBERS_ACTIONS.create, resource);
  const canUpdate = useCan(NUMBERS_ACTIONS.update, resource);
  const canBind = useCan(NUMBERS_ACTIONS.bind, resource);
  const canDelete = useCan(NUMBERS_ACTIONS.delete, resource);

  const projectIdFilter = searchParams.get('projectId') ?? ALL;
  const rawStatus = searchParams.get('isActive');
  const statusFilter = rawStatus === 'true' || rawStatus === 'false' ? rawStatus : ALL;

  const [page, setPage] = useState(1);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<PhoneNumberView | null>(null);
  const [bindTarget, setBindTarget] = useState<PhoneNumberView | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PhoneNumberView | null>(null);

  const numbersQuery = useListNumbersQuery(
    {
      page,
      limit: PAGE_SIZE,
      projectId: projectIdFilter === ALL ? undefined : projectIdFilter,
      isActive: statusFilter === ALL ? undefined : statusFilter,
    },
    { skip: canRead.allowed !== true },
  );
  const factoriesQuery = useGetFactoriesQuery({ orgId }, { skip: canRead.allowed !== true });
  const projectsQuery = useGetOrgProjectsQuery(orgId, {
    skip: canRead.allowed !== true,
  });
  const [deleteNumber, { isLoading: deleting }] = useDeleteNumberMutation();

  const agentNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const factory of factoriesQuery.data ?? []) {
      map.set(factory.id, factory.name);
    }
    return map;
  }, [factoriesQuery.data]);

  const projectNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const project of projectsQuery.data ?? []) {
      const id = docId(project);
      if (id) {
        map.set(id, project.name ?? id);
      }
    }
    return map;
  }, [projectsQuery.data]);

  function setFilter(key: 'projectId' | 'isActive', value: string) {
    const next = new URLSearchParams(searchParams);
    if (value === ALL) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next, { replace: true });
    setPage(1);
  }

  if (canRead.allowed === false) {
    return <PolicyDeniedView />;
  }
  if (canRead.allowed === undefined) {
    // Verdict unresolved — controls must not flash (§14.5 stance).
    return <LoadingRows label="Loading numbers…" columns={8} rows={4} />;
  }

  const rows = numbersQuery.data?.data ?? [];
  const total = numbersQuery.data?.pagination.total ?? 0;
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const selectedProjectForForm = projectIdFilter === ALL ? undefined : projectIdFilter;

  const registerButton = (
    <Button onClick={() => setRegisterOpen(true)}>
      <Phone className="mr-2 h-4 w-4" aria-hidden="true" />
      Register number
    </Button>
  );

  async function confirmDelete() {
    if (!deleteTarget) {
      return;
    }
    try {
      await deleteNumber(deleteTarget.id).unwrap();
      toast.success('Number deleted.');
      setDeleteTarget(null);
    } catch (error) {
      toast.error(numbersErrorMessage(error, "Couldn't delete number."));
      setDeleteTarget(null);
    }
  }

  return (
    <div className="space-y-4">
      <Breadcrumb
        items={[
          { label: 'Organization', to: `/organizations/${orgId}` },
          { label: 'Phone numbers' },
        ]}
      />

      <PageHeader
        title="Phone numbers"
        description="Phone numbers registered for this organization and the agents they forward calls to."
        titleId="numbers-title"
        actions={
          <Can action={NUMBERS_ACTIONS.create} resource={resource}>
            {registerButton}
          </Can>
        }
      />

      <div className="flex flex-wrap items-center justify-end gap-2">
        <Select value={projectIdFilter} onValueChange={(value) => setFilter('projectId', value)}>
          <SelectTrigger aria-label="Filter by project" className="w-48">
            <SelectValue placeholder="All projects" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All projects</SelectItem>
            {(projectsQuery.data ?? [])
              .filter((project) => project.isActive !== false)
              .map((project) => {
                const id = docId(project);
                return id ? (
                  <SelectItem key={id} value={id}>
                    {project.name ?? id}
                  </SelectItem>
                ) : null;
              })}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={(value) => setFilter('isActive', value)}>
          <SelectTrigger aria-label="Filter by status" className="w-40">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All statuses</SelectItem>
            <SelectItem value="true">Active</SelectItem>
            <SelectItem value="false">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {numbersQuery.isLoading ? (
        <LoadingRows label="Loading numbers…" columns={8} rows={4} />
      ) : numbersQuery.isError ? (
        <QueryErrorView
          error={numbersQuery.error}
          label="numbers"
          onRetry={() => void numbersQuery.refetch()}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Phone />}
          title="No phone numbers"
          description={
            canCreate.allowed === false
              ? 'Ask your administrator to allocate a number for this organization.'
              : 'Register the first phone number for this organization.'
          }
          action={
            <Can action={NUMBERS_ACTIONS.create} resource={resource}>
              {registerButton}
            </Can>
          }
        />
      ) : (
        <>
          <NumbersTable
            numbers={rows}
            agentNameById={agentNameById}
            factoriesLoading={factoriesQuery.isLoading}
            projectNameById={projectNameById}
            canUpdate={canUpdate.allowed}
            canBind={canBind.allowed}
            canDelete={canDelete.allowed}
            onEdit={setEditTarget}
            onBind={setBindTarget}
            onDelete={setDeleteTarget}
          />
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {rangeStart}–{rangeEnd} of {total}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((value) => Math.max(1, value - 1))}
                disabled={page <= 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((value) => Math.min(pageCount, value + 1))}
                disabled={page >= pageCount}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}

      <RegisterNumberDialog
        open={registerOpen}
        onOpenChange={setRegisterOpen}
        orgId={orgId}
        defaultProjectId={selectedProjectForForm}
      />

      {editTarget ? (
        <EditNumberDialog
          open
          onOpenChange={(next) => {
            if (!next) {
              setEditTarget(null);
            }
          }}
          orgId={orgId}
          number={editTarget}
          agentName={editTarget.factoryId ? agentNameById.get(editTarget.factoryId) : undefined}
        />
      ) : null}

      {bindTarget ? (
        <BindAgentDialog
          open
          onOpenChange={(next) => {
            if (!next) {
              setBindTarget(null);
            }
          }}
          orgId={orgId}
          number={bindTarget}
        />
      ) : null}

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open && !deleting) {
            setDeleteTarget(null);
          }
        }}
        title="Delete number?"
        description={`This permanently removes ${deleteTarget?.number ?? ''} from the organization. Any agent binding is lost.`}
        confirmLabel="Delete number"
        variant="destructive"
        isLoading={deleting}
        onConfirm={() => void confirmDelete()}
      />
    </div>
  );
}
